// ═══ MOUNT ════════════════════════════════════════════════
// Entry point that stitches the ported builder modules together. Mirrors the
// legacy/EMR Forms.html top-level script: it renders BUILDER_TEMPLATE into the
// host element, builds the shared `Ctx` (the object that stands in for the
// legacy single-closure scope), wires each module's attachXxx() in dependency
// order, defines the api/server-status glue (setServerStatus / checkServerStatus
// / debounced syncJson auto-save), runs the legacy initApp() load sequence, and
// starts the 30s /api/ping poll.
//
// Returns a cleanup() that clears the interval + save timer and removes the
// document-level keydown listener, so React 18 StrictMode's mount→cleanup→mount
// double-invoke neither leaks nor double-fires. Idempotent: each call resets the
// container's markup and re-wires from scratch.

import type { Ctx } from './engineTypes.ts';
import type { FormState } from './types.ts';
import { BUILDER_TEMPLATE } from './template.ts';
import { createToast } from './toast.ts';
import { createFormState, createUidGen, getQ as lookupQ, getS as lookupS } from './state.ts';
import { ping, getForm, getResponses, saveForm, getPublicForm } from './api.ts';
import { attachBuilderRender } from './builderRender.ts';
import { attachSections } from './sections.ts';
import { attachRuntime } from './runtime.ts';
import { attachResponses } from './responses.ts';
import { attachSavedForms } from './savedForms.ts';
import { attachPublish } from './publish.ts';
import { signOut } from '../../lib/auth-client.ts';

export function mountBuilder(container: HTMLElement, formId: string, opts: { public?: boolean } = {}): () => void {
  container.innerHTML = BUILDER_TEMPLATE;
  const $ = (id: string): any => container.querySelector('#' + id);
  const isPublic = !!opts.public;

  if (isPublic) {
    // Anonymous fill page: no builder chrome, no tab navigation, no sign-out —
    // the respondent only ever sees the runtime/preview pane. #view-builder
    // has no "hide" class in the template (it's the default active tab for
    // the authenticated builder), so without this it's fully visible —
    // fields palette, empty canvas, settings panel and all — until the
    // async form fetch resolves and switches tabs. Hide it (and the other
    // staff-only views) synchronously, before anything can paint.
    const topbar = container.querySelector('.topbar') as HTMLElement | null;
    if (topbar) topbar.style.display = 'none';
    ['view-builder', 'view-responses', 'view-saved'].forEach((id) => { const el = $(id); if (el) el.classList.add('hide'); });
    const runtimeView = $('view-runtime'); if (runtimeView) runtimeView.classList.remove('hide');
    ['rt-back', 'publish-btn-rt'].forEach((id) => { const el = $(id); if (el) el.style.display = 'none'; });
    // rt-next has no "hide" class by default either; use the class (not
    // inline style) so buildRuntime()'s later classList.remove('hide') can
    // still show it once the form actually loads.
    const rtNext = $('rt-next'); if (rtNext) rtNext.classList.add('hide');
    const rtTitle = $('rt-title'); if (rtTitle) rtTitle.textContent = 'Loading…';
  }

  const state: FormState = createFormState();
  const { uid, syncQSeq, reset } = createUidGen();
  const toast = createToast(container);

  // ── Build the shared Ctx. Cross-module function slots are populated by the
  // attachXxx() calls below; usage happens later (user interaction / initApp),
  // by which point every slot is filled. ──
  const ctx = {
    container,
    formId,
    public: isPublic,
    state,
    rt: { currentQ: null, currentSection: null, answers: {}, mode: 'step' as string },
    toast,
    secHistory: [] as string[],
    downloadReady: false,
  } as unknown as Ctx;

  // ── state.ts glue ──
  ctx.uid = uid;
  ctx.syncQSeq = () => syncQSeq(state.questions);
  ctx.resetUidSeq = () => reset();
  ctx.getQ = (id: any) => lookupQ(state, id);
  ctx.getS = (id: any) => lookupS(state, id);

  // ── api / server-status glue (was inline in the legacy JSON-SYNC section) ──
  function snapshot() {
    return {
      title: state.title, description: state.description, coverMedia: state.coverMedia,
      logo: state.logo, logos: state.logos, questions: state.questions,
      sections: state.sections, captureLocation: state.captureLocation,
      clientName: state.clientName, projectName: state.projectName, deliveredDate: state.deliveredDate,
    };
  }
  function setServerStatus(online: boolean) {
    const el = $('server-status'), lbl = $('server-status-lbl');
    if (!el || !lbl) return;
    if (online) { el.className = 'server-status connected'; lbl.textContent = 'Auto-saved'; el.title = 'Server connected — all changes saved to disk'; }
    else { el.className = 'server-status offline'; lbl.textContent = 'Offline'; el.title = 'Server not running — open start.bat to enable persistence'; }
  }
  async function checkServerStatus(): Promise<boolean> {
    // Legacy: fetch('/api/ping'); if(r.ok) online. axios rejects on non-2xx or
    // network error, so any failure lands here as offline.
    try { await ping(); setServerStatus(true); return true; } catch { /* offline */ }
    setServerStatus(false); return false;
  }
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  function saveFormToServer() {
    if (!formId) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      // Legacy: POST /api/forms/:id, on r.ok setServerStatus(true), catch → false.
      try { await saveForm(formId, snapshot()); setServerStatus(true); } catch { setServerStatus(false); }
    }, 800);
  }
  function syncJson() {
    const jsonEl = $('json'); if (jsonEl) jsonEl.value = JSON.stringify(snapshot(), null, 2);
    saveFormToServer();
    if (typeof ctx.setDownloadReady === 'function') ctx.setDownloadReady(false);
    if (typeof ctx.updatePublishState === 'function') ctx.updatePublishState();
  }
  ctx.setServerStatus = setServerStatus;
  ctx.checkServerStatus = checkServerStatus;
  ctx.syncJson = syncJson;

  // ── Wire modules (order: render/goto machinery, sections, runtime, responses,
  // saved-forms/settings). None invoke cross-module slots synchronously at
  // attach time, so ordering only needs to precede initApp's first render. ──
  attachBuilderRender(ctx);
  attachSections(ctx);
  attachRuntime(ctx);
  attachResponses(ctx);
  attachSavedForms(ctx);
  attachPublish(ctx);

  if (!isPublic) {
    $('btn-signout').addEventListener('click', () => {
      signOut({
        fetchOptions: {
          onSuccess: () => {
            window.location.href = '/login';
          },
        },
      });
    });
  }

  // ── INIT (legacy initApp) ──
  let disposed = false;
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  async function initPublic() {
    try {
      const form = await getPublicForm(formId);
      if (disposed) return;
      state.title = form.title || state.title;
      state.description = form.description || '';
      if (form.coverMedia) ctx.restoreCoverMedia(form.coverMedia);
      if (form.logos) state.logos = { a: form.logos.a || null, b: form.logos.b || null };
      if (form.logo && (state.logos as any)[form.logo]) state.logo = form.logo;
      state.questions = Array.isArray(form.questions) ? form.questions : [];
      state.sections = Array.isArray(form.sections) ? form.sections : [];
      state.captureLocation = form.captureLocation !== false;
      state.clientName = form.clientName || '';
      state.projectName = form.projectName || '';
      state.deliveredDate = form.deliveredDate || '';
      syncQSeq(state.questions);
      ctx.buildRuntime();
      ctx.show('runtime');
    } catch {
      // Unpublished, deleted, or link typo'd — same 404 either way so we
      // don't leak whether a given form id ever existed.
      const body = container.querySelector('#view-runtime .card-body');
      if (body) body.innerHTML = '<p class="muted" style="padding:48px 12px;text-align:center">This form is not available. The link may be incorrect, or the form may no longer be published.</p>';
      ctx.show('runtime');
    }
  }

  async function initApp() {
    const online = await checkServerStatus();
    if (disposed) return;
    if (online) {
      if (!formId) {
        // No form id in URL — send back to the dashboard (matches legacy).
        window.location.href = '/';
        return;
      }
      try {
        const form = await getForm(formId);
        if (disposed) return;
        if (form.title) { state.title = form.title; $('form-title').value = form.title; }
        if (form.description) { state.description = form.description; $('form-desc').value = form.description; }
        if (form.coverMedia) ctx.restoreCoverMedia(form.coverMedia);
        if (form.logos) state.logos = { a: form.logos.a || null, b: form.logos.b || null };
        if (form.logo && (state.logos as any)[form.logo]) state.logo = form.logo;
        if (Array.isArray(form.questions) && form.questions.length) { state.questions = form.questions; syncQSeq(state.questions); }
        if (Array.isArray(form.sections) && form.sections.length) state.sections = form.sections;
        if (form.captureLocation != null) { state.captureLocation = !!form.captureLocation; const tog = $('capture-location-toggle'); if (tog) tog.checked = state.captureLocation; }
        state.clientName = form.clientName || ''; const cnInp = $('client-name-inp'); if (cnInp) cnInp.value = state.clientName;
        state.projectName = form.projectName || ''; const pnInp = $('project-name-inp'); if (pnInp) pnInp.value = state.projectName;
        state.deliveredDate = form.deliveredDate || ''; const ddInp = $('delivered-date-inp'); if (ddInp) ddInp.value = state.deliveredDate;
        ctx.setPublishStatus(form.status || 'draft', form.publicSlug || null);
        const responses = await getResponses(formId);
        if (disposed) return;
        state.responses = responses;
        if (responses.length) $('resp-dot').classList.add('show');
        if (state.questions.length) toast('Form restored from disk.', 'ok', 2500);
      } catch {
        toast('Could not load saved data from server.', 'err', 5000);
      }
    } else {
      toast('Server not running — start start.bat to enable persistence.', 'info', 6000);
    }
    if (disposed) return;
    ctx.renderLogoPicker(); ctx.renderQuestions(); ctx.renderSections(); syncJson(); ctx.buildResponses();
    // Recheck server status every 30s (legacy setInterval(checkServerStatus, 30000)).
    pingInterval = setInterval(() => { checkServerStatus(); }, 30000);
  }
  if (isPublic) initPublic(); else initApp();

  // ── Cleanup (StrictMode-safe) ──
  return function cleanup() {
    disposed = true;
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    if (typeof ctx.teardownBuilderRender === 'function') ctx.teardownBuilderRender();
  };
}
